import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { CalendarOff, Receipt, Wallet, ArrowRight, Lock } from "lucide-react";
import { initials } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";
import { useCurrency, fmtMoney, fmtRange } from "@/lib/requests";
import { StatusChip } from "@/components/requests/RequestRows";
import DecisionDialog from "@/components/requests/DecisionDialog";

function Card({ icon: Icon, title, tint, testid, children, link = "/requests" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${tint}`}><Icon className="w-4 h-4" /></span>
          <h3 className="font-display font-semibold text-slate-900">{title}</h3>
        </div>
        <Link to={link} className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1" data-testid={`${testid}-link`}>View <ArrowRight className="w-3 h-3" /></Link>
      </div>
      {children}
    </div>
  );
}

export function MyRequestsCard() {
  const cur = useCurrency();
  const [leaves, setLeaves] = useState([]);
  const [reimbs, setReimbs] = useState([]);
  useEffect(() => {
    api.get("/leaves").then((r) => setLeaves(r.data));
    api.get("/reimbursements").then((r) => setReimbs(r.data));
  }, []);
  const year = String(new Date().getFullYear());
  const taken = leaves.filter((l) => l.status === "approved" && l.start_date.startsWith(year)).reduce((s, l) => s + l.days, 0);
  const pending = leaves.filter((l) => l.status === "pending").length;

  return (
    <Card icon={CalendarOff} title="My leave & reimbursements" tint="bg-sky-50 text-sky-600" testid="my-requests-card">
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="rounded-lg bg-slate-50 p-3"><p className="text-xl font-display font-bold text-slate-900" data-testid="my-leave-days-taken">{taken}</p><p className="text-[11px] text-slate-500">Leave days taken in {year}</p></div>
        <div className="rounded-lg bg-slate-50 p-3"><p className="text-xl font-display font-bold text-slate-900" data-testid="my-leave-pending">{pending}</p><p className="text-[11px] text-slate-500">Leave requests pending</p></div>
      </div>
      <div className="space-y-2">
        {leaves.slice(0, 3).map((l) => (
          <div key={l.id} className="flex items-center gap-2 text-sm"><CalendarOff className="w-3.5 h-3.5 text-slate-400 shrink-0" /><span className="flex-1 truncate text-slate-700">{fmtRange(l.start_date, l.end_date)}</span><StatusChip status={l.status} /></div>
        ))}
        {reimbs.slice(0, 4).map((r) => (
          <div key={r.id} className="flex items-center gap-2 text-sm"><Receipt className="w-3.5 h-3.5 text-slate-400 shrink-0" /><span className="flex-1 truncate text-slate-700">{fmtMoney(r.amount, cur)} · {r.category}</span><StatusChip status={r.status} /></div>
        ))}
        {!leaves.length && !reimbs.length && <p className="text-sm text-slate-400">No requests yet.</p>}
      </div>
    </Card>
  );
}

export function OnLeaveTodayCard() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/leaves/today").then((r) => setRows(r.data)); }, []);
  return (
    <Card icon={CalendarOff} title={`On leave today (${rows.length})`} tint="bg-amber-50 text-amber-600" testid="on-leave-today-card">
      <div className="space-y-2.5">
        {rows.map((l) => (
          <div key={l.id} className="flex items-center gap-2.5" data-testid={`on-leave-${l.id}`}>
            <Avatar className="w-8 h-8"><AvatarImage src={l.avatar} /><AvatarFallback className="bg-slate-100 text-slate-500 text-[10px]">{initials(l.user_name)}</AvatarFallback></Avatar>
            <div className="min-w-0"><p className="text-sm font-medium text-slate-900 truncate">{l.user_name}</p><p className="text-xs text-slate-500">{fmtRange(l.start_date, l.end_date)}</p></div>
          </div>
        ))}
        {!rows.length && <p className="text-sm text-slate-400">Everyone is in today.</p>}
      </div>
    </Card>
  );
}

export function PendingReimbursementsCard() {
  const cur = useCurrency();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/reimbursements", { params: { scope: "all", status: "pending" } }).then((r) => setRows(r.data)); }, []);
  const total = rows.reduce((s, r) => s + r.amount, 0);
  return (
    <Card icon={Receipt} title={`Pending reimbursements (${rows.length})`} tint="bg-violet-50 text-violet-600" testid="pending-reimbursements-card">
      {rows.length > 0 && <p className="text-xs text-slate-500 mb-3">Total awaiting review: <span className="font-semibold text-slate-900" data-testid="pending-reimb-total">{fmtMoney(total, cur)}</span></p>}
      <div className="space-y-2 max-h-[240px] overflow-y-auto scroll-thin">
        {rows.map((r) => (
          <div key={r.id} className="flex items-center gap-2 text-sm"><span className="flex-1 truncate text-slate-700"><b className="font-medium text-slate-900">{r.user_name}</b> · {r.category}</span><span className="font-semibold text-slate-900">{fmtMoney(r.amount, cur)}</span></div>
        ))}
        {!rows.length && <p className="text-sm text-slate-400">Nothing waiting for review.</p>}
      </div>
    </Card>
  );
}

export function ReimbursementSummaryCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [closing, setClosing] = useState(null);
  const load = useCallback(() => api.get("/reimbursements/summary").then((r) => setData(r.data)), []);
  useEffect(() => { if (user?.is_owner) load(); }, [user, load]);
  if (!user?.is_owner || !data) return null;
  const cur = data.currency;
  const s = data.by_status;
  const stats = [
    ["Pending review", s.pending, "text-amber-600"], ["Approved, to close", s.approved, "text-emerald-600"],
    ["Closed", s.closed, "text-slate-600"], ["Rejected", s.rejected, "text-red-600"],
  ];
  return (
    <Card icon={Wallet} title="Reimbursement summary" tint="bg-emerald-50 text-emerald-600" testid="reimbursement-summary-card">
      <div className="grid grid-cols-2 gap-2.5 mb-3">
        {stats.map(([label, v, cls]) => (
          <div key={label} className="rounded-lg bg-slate-50 p-3">
            <p className={`text-base font-display font-bold ${cls}`}>{fmtMoney(v.amount, cur)}</p>
            <p className="text-[11px] text-slate-500">{label} · {v.count}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500 mb-3">Closed this month: <span className="font-semibold text-slate-900" data-testid="summary-closed-month">{fmtMoney(data.closed_this_month, cur)}</span></p>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Approved — ready to close</p>
      <div className="space-y-2 max-h-[260px] overflow-y-auto scroll-thin">
        {data.awaiting_close.map((r) => (
          <div key={r.id} className="flex items-center gap-2 text-sm" data-testid={`summary-row-${r.id}`}>
            <span className="flex-1 min-w-0 truncate text-slate-700"><b className="font-medium text-slate-900">{r.user_name}</b> · {r.category}</span>
            <span className="font-semibold text-slate-900 shrink-0">{fmtMoney(r.amount, cur)}</span>
            <Button size="sm" onClick={() => setClosing(r)} className="h-7 px-2 bg-slate-900 hover:bg-slate-800 shrink-0" data-testid={`summary-close-${r.id}`}><Lock className="w-3 h-3 mr-1" />Close</Button>
          </div>
        ))}
        {!data.awaiting_close.length && <p className="text-sm text-slate-400">Nothing to close.</p>}
      </div>
      {closing && <DecisionDialog item={closing} kind="reimbursements" mode="close" onClose={() => setClosing(null)} onDone={load} />}
    </Card>
  );
}
